import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

function Shell({ title, subtitle, children, testid }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50" data-testid={testid}>
      <Card className="w-full max-w-md p-8">
        <div className="flex items-center gap-3 mb-6">
          <img src="/safer-highways-logo.png" alt="Safer Highways Inc" className="h-10 w-10 object-contain" />
          <div><div className="tag-mono text-blue-700">SAFER HIGHWAYS INC</div><div className="text-sm font-semibold text-slate-900">Consortium Portal</div></div>
        </div>
        <h1 className="text-2xl font-extrabold font-heading text-slate-900">{title}</h1>
        <p className="text-sm text-slate-600 mt-1 mb-6">{subtitle}</p>
        {children}
        <div className="mt-6 text-xs text-slate-500"><Link to="/login" className="hover:text-slate-800">← Back to sign in</Link></div>
      </Card>
    </div>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email, origin_url: window.location.origin });
      setSent(true);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  return (
    <Shell title="Forgot your password?" subtitle="Enter your email and we'll send you a link to choose a new one." testid="forgot-password-page">
      {sent ? (
        <div className="rounded-md bg-emerald-50 border border-emerald-200 p-4 text-sm text-emerald-800" data-testid="forgot-password-sent">
          If an account exists for <b>{email}</b>, a reset link is on its way. It expires in 1 hour — check your spam folder if you don't see it.
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div><Label htmlFor="email">Email</Label><Input id="email" type="email" required autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email-input" /></div>
          <Button type="submit" disabled={loading} className="w-full bg-blue-700 hover:bg-blue-800" data-testid="forgot-submit-button">{loading ? "Sending…" : "Send reset link"}</Button>
        </form>
      )}
    </Shell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { toast.error("Passwords do not match"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated — please sign in");
      navigate("/login");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  return (
    <Shell title="Choose a new password" subtitle="Use at least 8 characters." testid="reset-password-page">
      {!token ? (
        <div className="rounded-md bg-rose-50 border border-rose-200 p-4 text-sm text-rose-800" data-testid="reset-missing-token">This reset link is missing its token. <Link to="/forgot-password" className="underline">Request a new one</Link>.</div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div><Label htmlFor="pw">New password</Label><Input id="pw" type="password" required minLength={8} autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="reset-password-input" /></div>
          <div><Label htmlFor="pw2">Confirm password</Label><Input id="pw2" type="password" required minLength={8} autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="reset-confirm-input" /></div>
          <Button type="submit" disabled={loading} className="w-full bg-blue-700 hover:bg-blue-800" data-testid="reset-submit-button">{loading ? "Saving…" : "Set new password"}</Button>
        </form>
      )}
    </Shell>
  );
}
